/**
 * 録音ロック判定ユーティリティ
 * 土日祝を含む連休期間中の提出制限を管理
 */

/**
 * 休日ブロック（連続する休日）を検出
 * @param {Set} officialHolidays - 公式休日のSet
 * @param {number} year - 対象年
 * @returns {Array<Array<string>>} 休日ブロックの配列（各ブロックは日付文字列の配列）
 */
export const findHolidayBlocks = (officialHolidays, year = new Date().getFullYear()) => {
  const holidayBlocks = []
  // 1年分の日付を UTC で列挙（タイムゾーン影響を完全排除）
  const allDates = []
  const start = new Date(Date.UTC(year, 0, 1))
  const end   = new Date(Date.UTC(year, 11, 31))
  for (let dt = new Date(start); dt <= end; dt.setUTCDate(dt.getUTCDate() + 1)) {
    const y = dt.getUTCFullYear()
    const m = String(dt.getUTCMonth() + 1).padStart(2, '0')
    const d = String(dt.getUTCDate()).padStart(2, '0')
    allDates.push(`${y}-${m}-${d}`)
  }
  
  let currentBlock = []
  
  for (const date of allDates) {
    const isHoliday = isDateHoliday(date, officialHolidays)
    
    if (isHoliday) {
      currentBlock.push(date)
    } else if (currentBlock.length > 0) {
      // 休日が途切れた時点でブロック登録（単日祝日も1ブロック）
      if (currentBlock.length >= 1) {
        holidayBlocks.push([...currentBlock])
      }
      currentBlock = []
    }
  }
  
  // 年末の休日ブロックを処理
  if (currentBlock.length >= 1) {
    holidayBlocks.push([...currentBlock])
  }
  
  return holidayBlocks
}

/**
 * 指定日が休日かどうかを判定
 * @param {string} date - YYYY-MM-DD形式の日付
 * @param {Set} officialHolidays - 公式休日のSet
 * @returns {boolean} 休日かどうか
 */
export const isDateHoliday = (date, officialHolidays) => {
  // YYYY-MM-DD を「UTCの0時」として評価（環境依存のズレを排除）
  const [y, m, d] = date.split('-').map(Number)
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  
  // 土日は自動的に休日
  if (day === 0 || day === 6) return true
  
  // DBの特別休日をチェック（Set化でO(1)判定）
  // officialHolidaysがnullまたはSetでない場合は土日のみの判定
  if (!officialHolidays || !(officialHolidays instanceof Set)) return false
  
  return officialHolidays.has(date)
}

/**
 * 録音がロックされているかどうかを判定
 * @param {string} today - 今日の日付（YYYY-MM-DD）
 * @param {Array<string>} submittedDates - 提出済み日付の配列
 * @param {Array<Array<string>>} holidayBlocks - 休日ブロックの配列
 * @returns {boolean} ロックされているかどうか
 */
export const isRecordingLocked = (today, submittedDates, holidayBlocks) => {
  // 今日が含まれる休日ブロックを検索
  for (const block of holidayBlocks) {
    if (block.includes(today)) {
      // このブロック内に既に提出があるかチェック
      const hasSubmissionInBlock = block.some(date => submittedDates.includes(date))
      return hasSubmissionInBlock // 既にブロック内に1回提出があればロック
    }
  }
  return false // 休日ブロックに含まれない場合はロックなし
}

/**
 * 今日が休日ブロック内の最初の日かどうかを判定
 * @param {string} today - 今日の日付（YYYY-MM-DD）
 * @param {Array<Array<string>>} holidayBlocks - 休日ブロックの配列
 * @returns {boolean} 休日ブロックの最初の日かどうか
 */
export const isFirstDayOfHolidayBlock = (today, holidayBlocks) => {
  for (const block of holidayBlocks) {
    if (block.includes(today) && block[0] === today) {
      return true
    }
  }
  return false
}

/**
 * 休日ブロックの情報を取得
 * @param {string} today - 今日の日付（YYYY-MM-DD）
 * @param {Array<Array<string>>} holidayBlocks - 休日ブロックの配列
 * @returns {Object|null} 休日ブロック情報（開始日、終了日、日数）
 */
export const getHolidayBlockInfo = (today, holidayBlocks) => {
  for (const block of holidayBlocks) {
    if (block.includes(today)) {
      return {
        startDate: block[0],
        endDate: block[block.length - 1],
        days: block.length,
        isFirstDay: block[0] === today,
        isLastDay: block[block.length - 1] === today
      }
    }
  }
  return null
}

/**
 * 録音ロックの理由メッセージを生成
 * @param {string} today - 今日の日付（YYYY-MM-DD）
 * @param {Array<Array<string>>} holidayBlocks - 休日ブロックの配列
 * @returns {string} ロック理由メッセージ
 */
export const getLockReasonMessage = (today, holidayBlocks) => {
  const blockInfo = getHolidayBlockInfo(today, holidayBlocks)
  
  if (!blockInfo) {
    return ''
  }
  
  const fmt = (ymd) => {
    const [y, m, d] = ymd.split('-').map(Number)
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('ja-JP', {
      month: 'short',
      day: 'numeric',
      timeZone: 'Asia/Tokyo'
    })
  }
  const startDate = fmt(blockInfo.startDate)
  const endDate = fmt(blockInfo.endDate)
  
  if (blockInfo.days === 1) {
    return `${startDate}は休日のため、提出は1回のみ可能です。`
  }
  if (blockInfo.days === 2) {
    return `${startDate}〜${endDate}の連休期間中は提出は1回のみ可能です。`
  }
  return `${startDate}〜${endDate}の${blockInfo.days}連休期間中は提出は1回のみ可能です。`
}
